import { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import useFetchInterviews from "../../hooks/useFetchInterviews";
import useFetchSinglePack from "../../hooks/useFetchSinglePack";
import useCheckPackAccess from "../../hooks/useCheckPackAccess";
import checkTestStatus from "../../hooks/useCheckTestStatus";
import { auth } from "../../services/firebase";
import BuyPackPopup from "../../components/BuyPackPopup";
import InterviewInstructionPopup from "../../components/InterviewInstructionPopup"; // ✅ new import
import "./InterviewListScreen.css";

export default function InterviewListScreen() {
    const { domainId, packId, packName } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const packFromNav = location.state?.pack;
    const { pack: fetchedPack, loadingPack } = useFetchSinglePack(domainId, packId);
    const pack = packFromNav || fetchedPack;

    const { interviews, loadingInterviews } = useFetchInterviews(domainId, packId);
    const { hasAccess, expired, loading } = useCheckPackAccess(domainId, packId);

    const [showPopup, setShowPopup] = useState(false);
    const [showInstruction, setShowInstruction] = useState(false); // ✅ new
    const [selectedInterview, setSelectedInterview] = useState(null); // ✅ store selected interview
    
    const [statusMap, setStatusMap] = useState({});

    useEffect(() => {
        if (!interviews || interviews.length === 0) return;

        const fetchStatuses = async () => {
            const map = {};
            for (const interview of interviews) {
                try {
                    const res = await checkTestStatus(domainId, packId, interview.id);
                    map[interview.id] = res?.status || "new";
                } catch (err) {
                    console.error("Status fetch failed:", err);
                    map[interview.id] = "new";
                }
            }
            setStatusMap(map);
        };

        fetchStatuses();
    }, [interviews]);


    const handleInterviewClick = (interviewId, isFree) => {
        const user = auth.currentUser;
        if (!user) {
            alert("Please sign in to continue.");
            return;
        }

        if (!isFree && (!hasAccess || expired)) {
            setShowPopup(true);
            return;
        }

        const status = statusMap[interviewId];

        // If completed → directly navigate to Result
        if (status === "finished") {
            navigate(`/user/checkresult/${domainId}/${packId}/${interviewId}`);
            return;
        }

        // Otherwise → show instruction popup
        setSelectedInterview(interviewId);
        setShowInstruction(true);
    };


    const handleBuyClick = () => {
        setShowPopup(false);
        if (!pack) {
            alert("Unable to load pack details. Please try again.");
            return;
        }
        navigate(`/user/billing/${domainId}/${packId}`, { state: { pack } });
    };

    const handleStartInterview = async () => {
        setShowInstruction(false);
        try {
            const status = await checkTestStatus(domainId, packId, selectedInterview);
            if (status && status.status === "finished") {
                navigate(`/user/checkresult/${domainId}/${packId}/${selectedInterview}`);
            } else {
                navigate(`/user/test/${domainId}/${packId}/${selectedInterview}`);
            }
        } catch (err) {
            console.error("Error checking status:", err);
            alert("Unable to check test status. Please try again.");
        }
    };

    if (loadingInterviews || loadingPack) {
        return <p className="loading-text">Loading interviews...</p>;
    }

    return (
        <div className="interview-page">
            <header className="interview-header">
                <h1>{pack?.name || packName}</h1>
                <p className="subtitle">Click on any interview to start.</p>
            </header>

            {interviews.length === 0 ? (
                <p className="no-interviews">No interviews available in this pack.</p>
            ) : (
                <div className="interview-grid">
                    {interviews.map((i) => (
                        <div
                            key={i.id}
                            className="interview-card"
                            onClick={() => handleInterviewClick(i.id, i.isFree)}
                        >
                            <div className="interview-header-section">
                                <h3>{i.name}</h3>
                            </div>

                            <p className="requirements">
                                <strong>Requirements:</strong> {i.requirements || "Not specified."}
                            </p>

                            <div className="interview-footer">
                                <button className="start-btn">
                                    {statusMap[i.id] === "finished" ? "View Result" : "Start"}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* 💳 Buy Pack Popup */}
            <BuyPackPopup
                visible={showPopup}
                onClose={() => setShowPopup(false)}
                onBuy={handleBuyClick}
                packName={pack?.name || packName}
                totalInterviews={interviews.length}
                price={pack?.price}
                validity={pack?.validity}
            />

            {/* 🧾 Instruction Popup */}
            <InterviewInstructionPopup
                visible={showInstruction}
                onClose={() => setShowInstruction(false)}
                onStart={handleStartInterview}
            />
        </div>
    );
}
